import React from "react";
import { PlanogramProductSchema } from "@labeling-tools/api/atlas";
import { cn } from "../../../utils/plano-related.utils";
import {
  getLabelDimension,
  PlanoDimension,
} from "../../../utils/plano-related.utils";
import Color from "color";
import ColorHash from "color-hash";
import { useRef, useState, useCallback } from "react";
import { Loader } from "@mantine/core";
import { IconPhotoOff } from "@tabler/icons-react";
import {
  activeLabelBoxWidth,
  useActivePlanoProductInfoStore,
} from "../../use-active-plano-product-info-store";
import { formatMoney } from "../../../utils/helpers";

const colorHash = new ColorHash({ saturation: 0.9 });

interface Props {
  planoDimension: PlanoDimension;
  planogramProduct: PlanogramProductSchema;
  isSelected: boolean;
  isWarned?: boolean;
  callback?: (product_id: string | null) => void;
  showImage?: boolean;
  currentScale?: number;
  imageUrlOverride?: string;
  tooltipImageUrlOverride?: string;
}

export const OneLabelBox = (props: Props) => {
  const {
    planoDimension,
    planogramProduct,
    isSelected,
    isWarned,
    showImage,
    currentScale,
    imageUrlOverride,
    tooltipImageUrlOverride,
  } = props;
  const effectiveImageUrl = imageUrlOverride ?? planogramProduct.image_url;
  const effectiveTooltipUrl =
    tooltipImageUrlOverride ?? imageUrlOverride ?? planogramProduct.image_url;

  const labelDimension = getLabelDimension(planogramProduct);

  const left =
    ((labelDimension.x0 - planoDimension.x0) / planoDimension.w) * 100 + "%";
  const top =
    ((labelDimension.y0 - planoDimension.y0) / planoDimension.h) * 100 + "%";
  const width = (labelDimension.w / planoDimension.w) * 100 + "%";
  const height = (labelDimension.h / planoDimension.h) * 100 + "%";
  const borderColor = isSelected ? "red" : isWarned ? "#eab308" : "transparent";

  const bgColor = colorHash.hex(planogramProduct.description);
  const backgroundColor = Color(bgColor).alpha(0.1).toString();
  const bgColorHover = Color(bgColor).alpha(0.3).toString();

  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPlacement, setTooltipPlacement] = useState<
    "top" | "bottom" | "left" | "right"
  >("top");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [tooltipImageLoaded, setTooltipImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => setImageLoaded(true), []);
  const handleImageError = useCallback(() => setImageError(true), []);
  const handleTooltipImageLoad = useCallback(
    () => setTooltipImageLoaded(true),
    [],
  );

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = bgColorHover;
    if (effectiveImageUrl) {
      hoverTimerRef.current = setTimeout(() => {
        const el = boxRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const container = document.getElementById("planogram-view-container");
        const cr = container?.getBoundingClientRect();
        if (!cr) return;

        const tooltipSize = 200;
        const gap = 8;
        const needed = tooltipSize + gap;
        const halfTooltip = tooltipSize / 2;

        // Available space from each box edge to the container edge
        const spaceAbove = rect.top - cr.top;
        let spaceBelow = cr.bottom - rect.bottom;
        let spaceRight = cr.right - rect.right;
        const spaceLeft = rect.left - cr.left;

        // Reduce available space if tooltip would overlap the minimap (bottom-right corner)
        // Minimap is 200x120 at bottom:8px right:8px
        const minimapW = 200;
        const minimapH = 120;
        const minimapGap = 8;
        const boxRight = cr.right - rect.right;
        const boxBottom = cr.bottom - rect.bottom;
        const isNearMinimapX = boxRight < minimapW + minimapGap + tooltipSize;
        const isNearMinimapY = boxBottom < minimapH + minimapGap + tooltipSize;
        if (isNearMinimapX && boxBottom < minimapH + minimapGap) {
          spaceRight = Math.min(
            spaceRight,
            cr.right - rect.right - minimapW - minimapGap * 2,
          );
        }
        if (isNearMinimapY && boxRight < minimapW + minimapGap) {
          spaceBelow = Math.min(
            spaceBelow,
            cr.bottom - rect.bottom - minimapH - minimapGap * 2,
          );
        }

        // For top/bottom: tooltip is centered horizontally, check it fits
        const boxCenterX = rect.left + rect.width / 2 - cr.left;
        const hFits =
          boxCenterX >= halfTooltip && cr.width - boxCenterX >= halfTooltip;

        // For left/right: tooltip is centered vertically, check it fits
        const boxCenterY = rect.top + rect.height / 2 - cr.top;
        const vFits =
          boxCenterY >= halfTooltip && cr.height - boxCenterY >= halfTooltip;

        if (spaceAbove >= needed && hFits) {
          setTooltipPlacement("top");
        } else if (spaceBelow >= needed && hFits) {
          setTooltipPlacement("bottom");
        } else if (spaceRight >= needed && vFits) {
          setTooltipPlacement("right");
        } else if (spaceLeft >= needed && vFits) {
          setTooltipPlacement("left");
        } else {
          // Fallback: pick the side with the most space
          const max = Math.max(spaceAbove, spaceBelow, spaceRight, spaceLeft);
          if (max === spaceLeft) setTooltipPlacement("left");
          else if (max === spaceRight) setTooltipPlacement("right");
          else if (max === spaceBelow) setTooltipPlacement("bottom");
          else setTooltipPlacement("top");
        }
        setShowTooltip(true);
      }, 500);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    e.currentTarget.style.backgroundColor = backgroundColor;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setShowTooltip(false);
    setTooltipImageLoaded(false);
  };

  return (
    <div
      ref={boxRef}
      className={cn(`absolute cursor-pointer`)}
      style={{
        top,
        left,
        width,
        height,
        backgroundColor,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={(e) => {
        mouseDownPos.current = { x: e.clientX, y: e.clientY };
        const { setMouseDownUuid } = useActivePlanoProductInfoStore.getState();
        setMouseDownUuid(planogramProduct.id);
      }}
      onClick={(e) => {
        // Ignore click if the mouse moved (user was dragging/panning)
        if (mouseDownPos.current) {
          const dx = e.clientX - mouseDownPos.current.x;
          const dy = e.clientY - mouseDownPos.current.y;
          if (dx * dx + dy * dy > 25) {
            mouseDownPos.current = null;
            return;
          }
        }
        mouseDownPos.current = null;

        const {
          setActiveLabelBoxInfo,
          productBoxInfo: labelBoxInfo,
          setMouseDownUuid,
          mouseDownUuid,
        } = useActivePlanoProductInfoStore.getState();
        const isClickAgain =
          mouseDownUuid === labelBoxInfo.planogram_product_id;
        setMouseDownUuid("");

        if (isClickAgain) {
          setActiveLabelBoxInfo({
            left: 0,
            top: 0,
            planogram_product_id: "",
            product_id: "",
          });
        } else {
          const rect = e.currentTarget.getBoundingClientRect();

          const parentEl = document.getElementById(
            "planogram-view-container",
          ) as HTMLDivElement | null;
          if (!parentEl) return;

          const parentRect = parentEl.getBoundingClientRect();

          const popoverWidth = Math.min(activeLabelBoxWidth, parentRect.width);
          const hasEnoughSpaceOnRight =
            rect.right + popoverWidth < window.innerWidth;

          const rawLeft = hasEnoughSpaceOnRight
            ? rect.right - parentRect.left + 4
            : rect.left - parentRect.left - popoverWidth + 20 - 4;

          setActiveLabelBoxInfo({
            top: Math.max(0, rect.top - parentRect.top),
            left: Math.max(
              0,
              Math.min(rawLeft, parentRect.width - popoverWidth),
            ),
            planogram_product_id: planogramProduct.id,
            product_id: planogramProduct.product_id,
          });
        }
      }}
    >
      {/* Border overlay rendered above image content */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          border: `${isSelected || isWarned ? 2 : 1}px solid ${borderColor}`,
        }}
      />
      {effectiveImageUrl && !imageError ? (
        <>
          {(showImage ?? true) && !imageLoaded && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ transform: `scale(${1 / (currentScale ?? 1)})` }}
            >
              <Loader size="xs" />
            </div>
          )}
          <div
            className={cn(
              "absolute inset-0 overflow-hidden transition-opacity duration-300",
              (showImage ?? true) && imageLoaded ? "opacity-100" : "opacity-0",
            )}
          >
            <img
              className="object-contain size-full"
              src={effectiveImageUrl}
              loading="lazy"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
        </>
      ) : (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ transform: `scale(${1 / (currentScale ?? 1)})` }}
        >
          <IconPhotoOff size={16} color="#9ca3af" />
        </div>
      )}

      {showTooltip &&
        effectiveTooltipUrl &&
        (() => {
          const inverseScale = 1 / (currentScale ?? 1);
          const tooltipSize = 200;
          const gap = 8 * inverseScale;

          const placementStyles: Record<string, React.CSSProperties> = {
            top: {
              bottom: `calc(100% + ${gap}px)`,
              left: "50%",
              transform: `translateX(-50%) scale(${inverseScale})`,
              transformOrigin: "bottom center",
            },
            bottom: {
              top: `calc(100% + ${gap}px)`,
              left: "50%",
              transform: `translateX(-50%) scale(${inverseScale})`,
              transformOrigin: "top center",
            },
            right: {
              left: `calc(100% + ${gap}px)`,
              top: "50%",
              transform: `translateY(-50%) scale(${inverseScale})`,
              transformOrigin: "center left",
            },
            left: {
              right: `calc(100% + ${gap}px)`,
              top: "50%",
              transform: `translateY(-50%) scale(${inverseScale})`,
              transformOrigin: "center right",
            },
          };

          const labelPosition =
            tooltipPlacement === "top"
              ? "-bottom-12"
              : tooltipPlacement === "bottom"
                ? "-top-12"
                : tooltipPlacement === "right"
                  ? "-left-6"
                  : "-right-6";

          return (
            <div
              className="absolute z-50 pointer-events-none rounded border border-gray-300 bg-white p-1 shadow-lg"
              style={{
                ...placementStyles[tooltipPlacement],
                width: tooltipSize,
                height: tooltipSize,
              }}
            >
              {!tooltipImageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader size="sm" />
                </div>
              )}
              <img
                src={effectiveTooltipUrl}
                className={cn(
                  "size-full object-contain",
                  !tooltipImageLoaded && "opacity-0",
                )}
                alt={planogramProduct.description || "Product"}
                onLoad={handleTooltipImageLoad}
              />
              {planogramProduct.description && (
                <div
                  className={`absolute ${labelPosition} left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 whitespace-nowrap text-xs`}
                >
                  <span className="rounded bg-gray-800 px-2 py-0.5 text-white">
                    {planogramProduct.description}
                  </span>
                  <span className="rounded bg-gray-800 px-2 py-0.5 text-white">
                    Facings: {planogramProduct.facings ?? 0}
                  </span>
                  {typeof planogramProduct.price === "number" && (
                    <span className="rounded bg-gray-800 px-2 py-0.5 text-white">
                      Price: {formatMoney(planogramProduct.price)}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })()}
    </div>
  );
};
